import { useState, useRef, useCallback, useEffect } from 'react';
import api from '@/api';
import {
  Upload, FileText, CheckCircle2, XCircle, Loader2,
  Sparkles, Wifi, X
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyles = {
  queued: { icon: FileText, color: 'text-muted-foreground', bg: 'bg-secondary/30', label: 'Queued' },
  extracting: { icon: Loader2, color: 'text-amber-400', bg: 'bg-amber-950/30', label: 'Extracting...' },
  done: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-950/30', label: 'Done' },
  error: { icon: XCircle, color: 'text-rose-400', bg: 'bg-rose-950/30', label: 'Error' },
};

export default function BulkWarrantyUpload({ projectId, onComplete }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [fileStatuses, setFileStatuses] = useState({});
  const [batchId, setBatchId] = useState(null);
  const [connected, setConnected] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const esRef = useRef(null);

  // SSE listener for bulk extraction progress
  useEffect(() => {
    if (!batchId || !projectId) return;

    const token = localStorage.getItem('fmiq_token');
    if (!token) return;

    const url = `${API_URL}/closeout/projects/${projectId}/events/stream?token=${encodeURIComponent(token)}`;
    let es;
    try { es = new EventSource(url); } catch { return; }
    esRef.current = es;

    es.addEventListener('connected', () => {
      setConnected(true);
    });

    es.addEventListener('warranty_extract_progress', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.batch_id !== batchId) return;
        setFileStatuses(prev => ({
          ...prev,
          [data.filename]: {
            status: data.status,
            equipment_name: data.equipment_name || '',
            vendor_name: data.vendor_name || '',
            ai_confidence: data.ai_confidence || 0,
            error: data.error || '',
            current: data.current,
            total: data.total,
          },
        }));
      } catch {}
    });

    es.addEventListener('warranty_extract_complete', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.batch_id !== batchId) return;
        setUploading(false);
        es.close();
        // Delay then refresh parent
        setTimeout(() => onComplete?.(), 1000);
      } catch {}
    });

    es.onerror = () => {
      setConnected(false);
    };

    return () => { es.close(); esRef.current = null; };
  }, [batchId, projectId, onComplete]);

  // Polling fallback
  useEffect(() => {
    if (!batchId || connected) return;
    // If SSE fails, poll the warranty list for changes
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/closeout/projects/${projectId}/warranties`);
        if (res.data?.length > 0) {
          // Check if all files are done
          const pending = files.filter(f => {
            const st = fileStatuses[f.name];
            return !st || st.status === 'queued' || st.status === 'extracting';
          });
          if (pending.length === 0 && files.length > 0) {
            clearInterval(interval);
            setUploading(false);
            onComplete?.();
          }
        }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [batchId, connected, files, fileStatuses, projectId, onComplete]);

  const handleFiles = useCallback((newFiles) => {
    const validFiles = Array.from(newFiles).filter(f =>
      /\.(pdf|png|jpg|jpeg|tiff|tif)$/i.test(f.name)
    );
    setFiles(prev => [...prev, ...validFiles]);
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeFile = (name) => {
    setFiles(prev => prev.filter(f => f.name !== name));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);

    // Set initial statuses
    const initial = {};
    files.forEach(f => { initial[f.name] = { status: 'queued' }; });
    setFileStatuses(initial);

    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res = await api.post(
        `/closeout/projects/${projectId}/bulk-upload-warranties`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setBatchId(res.data.batch_id);
    } catch (err) {
      console.error('Bulk upload failed:', err);
      setUploading(false);
      const errStatuses = {};
      files.forEach(f => { errStatuses[f.name] = { status: 'error', error: 'Upload failed' }; });
      setFileStatuses(errStatuses);
    }
  };

  const totalDone = Object.values(fileStatuses).filter(s => s.status === 'done').length;
  const totalErrors = Object.values(fileStatuses).filter(s => s.status === 'error').length;
  const totalFiles = files.length;
  const progress = totalFiles > 0 ? Math.round((totalDone + totalErrors) / totalFiles * 100) : 0;

  return (
    <div className="space-y-4" data-testid="bulk-warranty-upload">
      {/* Drop zone */}
      {!uploading && (
        <div
          className={`border-2 border-dashed rounded-sm p-8 text-center transition-colors cursor-pointer ${
            dragOver
              ? 'border-primary/60 bg-orange-950/20'
              : 'border-border hover:border-border bg-card/50'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          data-testid="warranty-drop-zone"
        >
          <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Drag & drop warranty certificates here
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            PDF, PNG, JPG — drop multiple files at once
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.tiff,.tif"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="warranty-bulk-input"
          />
        </div>
      )}

      {/* File queue */}
      {files.length > 0 && (
        <div className="space-y-2">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-heading uppercase tracking-wider text-muted-foreground">
                {totalFiles} file{totalFiles !== 1 ? 's' : ''}
              </span>
              {uploading && connected && (
                <span className="flex items-center gap-1 text-[9px] font-mono text-emerald-500">
                  <Wifi className="w-3 h-3" /> LIVE
                </span>
              )}
            </div>
            {!uploading && (
              <button
                onClick={handleUpload}
                data-testid="bulk-warranty-submit"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-heading uppercase tracking-wider rounded-sm transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Extract All with AI
              </button>
            )}
          </div>

          {/* Progress bar (visible during upload) */}
          {uploading && (
            <div className="space-y-1">
              <div className="h-2 bg-secondary rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-emerald-500 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
                <span>{totalDone} of {totalFiles} extracted</span>
                {totalErrors > 0 && <span className="text-rose-500">{totalErrors} error(s)</span>}
              </div>
            </div>
          )}

          {/* File list */}
          <div className="space-y-1 max-h-[300px] overflow-y-auto">
            {files.map((f) => {
              const st = fileStatuses[f.name] || { status: 'pending' };
              const style = statusStyles[st.status] || statusStyles.queued;
              const Icon = style.icon;

              return (
                <div
                  key={f.name}
                  className={`flex items-center gap-3 px-3 py-2 rounded-sm border border-border/50 ${style.bg} transition-colors`}
                  data-testid={`warranty-file-${f.name.replace(/[^a-z0-9]/gi, '_')}`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${style.color} ${st.status === 'extracting' ? 'animate-spin' : ''}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground/80 truncate font-mono">{f.name}</p>
                    {st.status === 'done' && st.equipment_name && (
                      <p className="text-[10px] text-emerald-500 mt-0.5 truncate">
                        {st.equipment_name} {st.vendor_name ? `— ${st.vendor_name}` : ''}
                        {st.ai_confidence > 0 && (
                          <span className="ml-2 text-muted-foreground">({Math.round(st.ai_confidence * 100)}%)</span>
                        )}
                      </p>
                    )}
                    {st.status === 'error' && st.error && (
                      <p className="text-[10px] text-rose-400 mt-0.5 truncate">{st.error}</p>
                    )}
                  </div>
                  <span className={`text-[9px] font-heading uppercase tracking-wider ${style.color} whitespace-nowrap`}>
                    {style.label}
                  </span>
                  {!uploading && (
                    <button onClick={(e) => { e.stopPropagation(); removeFile(f.name); }}
                      className="text-muted-foreground hover:text-muted-foreground shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
